// src/pages/About.jsx
// SEO-optimised about page for Dynesourcer
// Target keywords: buy-to-let mortgage broker UK, online mortgage sourcing platform,
// direct BTL mortgage sourcing, mortgage technology UK, specialist BTL broker,
// buy-to-let mortgage application online, property finance platform UK

import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Shield,
  Users,
  TrendingUp,
  Target,
  Check,
  ArrowRight,
  Zap,
  Search,
  BadgeCheck,
  FileText,
} from "lucide-react";
import { Link } from "react-router-dom";

const ABOUT_HERO_SRC = "/About_us.webp";

export default function About() {
  React.useEffect(() => {
    document.title = "About Dynesourcer | Revolutionising Buy-to-Let Mortgage Sourcing in the UK";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Dynesourcer has revolutionised how landlords source buy-to-let mortgages in the UK. Borrowers compare and apply online directly — our expert broker team fact-finds and submits the real application to the lender. No more waiting for a broker to call back."
      );
    }
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = ABOUT_HERO_SRC;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <TopNav />

      {/* ── Hero ── */}
      <section
        className="relative h-[500px] w-full overflow-hidden"
        aria-label="About Dynesourcer — revolutionising buy-to-let mortgage sourcing in the UK"
      >
        <h1 className="sr-only">
          About Dynesourcer — Revolutionising Buy-to-Let Mortgage Sourcing in the UK
        </h1>
        <img
          src={ABOUT_HERO_SRC}
          alt="Dynesourcer team — revolutionising buy-to-let mortgage sourcing and application in the UK"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-sky-900/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            About Dynesourcer
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight max-w-4xl">
            We've revolutionised<br className="hidden md:block" /> BTL mortgage sourcing in the UK
          </h2>
          <p className="mt-5 max-w-2xl text-white/90 text-lg leading-relaxed">
            Borrowers no longer have to wait for a broker to call them back with options.
            Dynesourcer lets landlords source, compare and apply for buy-to-let mortgages
            directly online — with our expert broker team handling the rest.
          </p>
        </div>
      </section>

      {/* ── The old way vs the Dynesourcer way ── */}
      <section
        className="py-16 md:py-24 bg-white"
        aria-label="How Dynesourcer changed buy-to-let mortgage sourcing"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              The old way of sourcing a BTL mortgage was broken
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              For years, landlords and property investors had no choice but to call a broker,
              answer questions over the phone, wait for them to search the market on your behalf,
              then hope the products they came back with were actually the best fit.
              Dynesourcer changed all of that.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Before */}
            <div className="rounded-2xl border border-red-100 bg-red-50 p-8">
              <h3 className="text-lg font-black text-red-700 mb-6 flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-600 text-sm font-black">✕</span>
                Before Dynesourcer
              </h3>
              <ul className="space-y-4">
                {[
                  "Call a broker and explain your requirements over the phone",
                  "Wait days for the broker to search the market and come back to you",
                  "Receive a shortlist of products — with no way to verify if they were truly best fit",
                  "Go back and forth answering more questions before anything was submitted",
                  "No visibility on what products were available or why certain ones were recommended",
                  "Application submitted to lender with no tracking or transparency",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-red-900">
                    <span className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400 font-bold">✕</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* After */}
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-8">
              <h3 className="text-lg font-black text-emerald-700 mb-6 flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-sm font-black">✓</span>
                The Dynesourcer way
              </h3>
              <ul className="space-y-4">
                {[
                  "Source buy-to-let mortgage products yourself — online, instantly, at any time",
                  "Compare rates, LTV, ICR, fees and eligibility criteria in real time",
                  "Only see products that actually match your borrower profile and property type",
                  "Submit an online application directly — no phone calls required",
                  "Our expert broker team receive your application and contact you to fact-find",
                  "We prepare and submit the full application to your chosen lender — you track it end-to-end",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-emerald-900">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it actually works ── */}
      <section
        className="py-16 md:py-20 bg-sky-50"
        aria-label="How Dynesourcer buy-to-let mortgage sourcing and application process works"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              How Dynesourcer works
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              We've built a platform that gives borrowers the transparency and control
              of self-service sourcing, backed by the expertise of a specialist BTL broker team.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <Search className="h-6 w-6" />,
                num: "1",
                title: "Borrower sources products directly",
                desc: "Landlords visit Dynesourcer, enter their requirements and instantly compare buy-to-let mortgage products matched to their eligibility — no broker call needed.",
              },
              {
                icon: <FileText className="h-6 w-6" />,
                num: "2",
                title: "Online application submitted",
                desc: "The borrower selects their preferred product and submits an online application through the platform. Free to enquire with no obligation at this stage.",
              },
              {
                icon: <Users className="h-6 w-6" />,
                num: "3",
                title: "Our broker team fact-finds",
                desc: "Our expert broker admin team receive the application, contact the borrower to fact-find, verify the details and confirm the best-fit product for their circumstances.",
              },
              {
                icon: <BadgeCheck className="h-6 w-6" />,
                num: "4",
                title: "Full application submitted to lender",
                desc: "We prepare and submit the complete mortgage application to the lender on the borrower's behalf — with full tracking through to offer and completion.",
              },
            ].map((step) => (
              <div
                key={step.num}
                className="rounded-xl bg-white border border-sky-100 p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sky-500 text-white font-black text-lg flex-shrink-0">
                    {step.num}
                  </div>
                  <div className="h-8 w-8 text-sky-400">{step.icon}</div>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/sourcing/buy-to-let-mortgage-sourcing"
              className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-8 py-4 text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-lg"
            >
              Try it now <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Mission & Vision ── */}
      <section
        className="py-16 md:py-20 bg-white"
        aria-label="Dynesourcer mission and vision for UK property finance"
      >
        <div className="mx-auto max-w-5xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">Our Mission &amp; Vision</h2>
          <p className="mt-4 text-slate-600 max-w-3xl mx-auto">
            We set out to fix a fundamental inefficiency in the UK mortgage market — the
            information gap between borrowers, brokers and lenders. Dynesourcer puts
            product data, eligibility criteria and the application process directly
            in the borrower's hands, for the first time.
          </p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 max-w-5xl mx-auto px-4">
          <div className="rounded-xl bg-white p-8 shadow-sm border border-sky-100 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-sky-500" />
              <h3 className="text-xl font-bold text-slate-900">Our Mission</h3>
            </div>
            <p className="text-slate-600 leading-relaxed">
              To give every landlord and property investor in the UK direct, transparent
              access to buy-to-let mortgage products matched to their eligibility — and
              to back that with specialist broker expertise that ensures every application
              has the best possible chance of completing.
            </p>
          </div>

          <div className="rounded-xl bg-white p-8 shadow-sm border border-sky-100 hover:shadow-lg transition-all">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="h-6 w-6 text-sky-500" />
              <h3 className="text-xl font-bold text-slate-900">Our Vision</h3>
            </div>
            <p className="text-slate-600 leading-relaxed">
              To become the UK's most trusted buy-to-let mortgage sourcing platform —
              where landlords, portfolio investors and property professionals come first
              to source, compare and apply, knowing a specialist broker team is with them
              every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* ── Core Values ── */}
      <section
        className="py-16 md:py-20 bg-slate-50"
        aria-label="Dynesourcer core values"
      >
        <div className="mx-auto max-w-6xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">Our Core Values</h2>
          <p className="mt-3 text-slate-600">
            The principles behind everything we build and every application we handle.
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: <Shield className="h-6 w-6" />,
                title: "Transparency first",
                desc: "Borrowers see the same products, rates and criteria that brokers see. No hidden deals, no information asymmetry — just the whole market, visible to everyone.",
              },
              {
                icon: <Zap className="h-6 w-6" />,
                title: "Technology + expertise",
                desc: "Smart sourcing technology gets borrowers to the right product fast. Our specialist broker team ensures the application that reaches the lender is complete, accurate and fit for purpose.",
              },
              {
                icon: <Check className="h-6 w-6" />,
                title: "Borrower in control",
                desc: "We've put the borrower at the centre of the process. You source, you choose, you apply — we handle the rest. No more waiting days to find out what's available.",
              },
            ].map((val) => (
              <div
                key={val.title}
                className="rounded-xl bg-white p-8 text-left border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600 mb-4">
                  {val.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{val.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-16 text-center text-white"
        style={{ backgroundColor: "rgb(2 132 199)" }}
        aria-label="Start sourcing buy-to-let mortgages on Dynesourcer"
      >
        <h2 className="text-3xl md:text-4xl font-black">
          Experience the new way to source a BTL mortgage
        </h2>
        <p className="mt-3 text-white/90 max-w-xl mx-auto">
          Compare buy-to-let mortgage products matched to your eligibility, apply online,
          and let our broker team handle the rest. Free to source and enquire.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/sourcing/buy-to-let-mortgage-sourcing"
            className="inline-flex items-center gap-2 bg-white text-sky-700 font-bold px-7 py-3.5 rounded-lg hover:bg-slate-100 transition-colors shadow-lg"
          >
            <Search className="h-5 w-5" /> Source BTL mortgages
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-white/25 transition-colors"
          >
            Contact us
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}